import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { toast } from 'react-toastify';
import { billsApi } from '../../services/smartBillApi';
import './CounterStatistics.css';

const formatCurrency = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(number);
};

const formatCurrencyFull = (value) => {
  const number = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
};

const formatCount = (value) => {
  return new Intl.NumberFormat('en-IN').format(Number(value) || 0);
};

const getTotalBills = (totals) => {
  if (!totals) return 0;
  const fromApi = Number(totals.totalBills);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  const fromDays = (totals.dailyTotals || []).reduce(
    (sum, day) => sum + (Number(day.billCount) || 0),
    0,
  );
  if (fromDays > 0) return fromDays;
  return Number.isFinite(fromApi) ? fromApi : 0;
};

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const EARLIEST_MONTH = '2018-01';

const formatDateLocal = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getTodayIST = () => {
  const now = new Date();
  const istDateStr = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
  const [year, month, day] = istDateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const toYearMonth = (date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

const getMonthBounds = (year, monthIndex) => {
  const start = new Date(year, monthIndex, 1);
  const end = new Date(year, monthIndex + 1, 0);
  return {
    from: formatDateLocal(start),
    to: formatDateLocal(end),
  };
};

const shiftYearMonth = (yyyyMm, delta) => {
  const [year, month] = yyyyMm.split('-').map(Number);
  return toYearMonth(new Date(year, month - 1 + delta, 1));
};

const isFullCalendarMonth = (from, to) => {
  if (!from || !to) return false;
  const [year, month] = from.split('-').map(Number);
  const bounds = getMonthBounds(year, month - 1);
  return from === bounds.from && to === bounds.to;
};

const formatMonthLabel = (yyyyMm) => {
  const [year, month] = (yyyyMm || '').split('-').map(Number);
  if (!year || !month) return '';
  return `${MONTH_NAMES[month - 1]} ${year}`;
};

const CounterStatistics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customFromDate, setCustomFromDate] = useState('');
  const [customToDate, setCustomToDate] = useState('');
  const [browsedMonth, setBrowsedMonth] = useState(() => toYearMonth(getTodayIST()));
  const [dailyTotals, setDailyTotals] = useState(null);
  const [serviceBreakdown, setServiceBreakdown] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [usedCouponsCount, setUsedCouponsCount] = useState(0);

  const navigate = useNavigate();

  const getDateRange = (period) => {
    // Use IST timezone for all date calculations
    const today = getTodayIST();
    
    switch (period) {
      case 'today':
        const todayStr = formatDateLocal(today);
        return { from: todayStr, to: todayStr };
      
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayStr = formatDateLocal(yesterday);
        return { from: yesterdayStr, to: yesterdayStr };
      
      case 'dayBeforeYesterday':
        const dayBefore = new Date(today);
        dayBefore.setDate(dayBefore.getDate() - 2);
        const dayBeforeStr = formatDateLocal(dayBefore);
        return { from: dayBeforeStr, to: dayBeforeStr };
      
      case 'thisWeek':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        return {
          from: formatDateLocal(weekStart),
          to: formatDateLocal(weekEnd)
        };
      
      case 'thisMonth':
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return {
          from: formatDateLocal(monthStart),
          to: formatDateLocal(monthEnd)
        };
      
      case 'previousMonth':
        const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return {
          from: formatDateLocal(prevMonthStart),
          to: formatDateLocal(prevMonthEnd)
        };
      
      case 'thisYear':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        const yearEnd = new Date(today.getFullYear(), 11, 31);
        return {
          from: formatDateLocal(yearStart),
          to: formatDateLocal(yearEnd)
        };
      
      case 'previousYear':
        const prevYearStart = new Date(today.getFullYear() - 1, 0, 1);
        const prevYearEnd = new Date(today.getFullYear() - 1, 11, 31);
        return {
          from: formatDateLocal(prevYearStart),
          to: formatDateLocal(prevYearEnd)
        };
      
      case 'custom':
        return { from: customFromDate, to: customToDate };

      case 'selectedMonth': {
        const [year, month] = browsedMonth.split('-').map(Number);
        return getMonthBounds(year, month - 1);
      }
      
      default:
        return { from: formatDateLocal(today), to: formatDateLocal(today) };
    }
  };

  const getPickerMonth = () => {
    if (selectedPeriod === 'selectedMonth') {
      return browsedMonth;
    }
    const { from } = getDateRange(selectedPeriod);
    if (from && /^\d{4}-\d{2}/.test(from)) {
      return from.slice(0, 7);
    }
    return browsedMonth;
  };

  const applyMonth = (yyyyMm) => {
    if (!yyyyMm) return;
    const currentMonth = toYearMonth(getTodayIST());
    let next = yyyyMm;
    if (next > currentMonth) next = currentMonth;
    if (next < EARLIEST_MONTH) next = EARLIEST_MONTH;
    setBrowsedMonth(next);
    setSelectedPeriod('selectedMonth');
  };

  const shiftMonth = (delta) => {
    applyMonth(shiftYearMonth(getPickerMonth(), delta));
  };

  const openBillsForRange = () => {
    const { from, to } = getDateRange(selectedPeriod);
    if (!from || !to) {
      toast.error('Please select a valid date range first.');
      return;
    }

    const params = new URLSearchParams();
    params.set('range', selectedPeriod || 'custom');
    params.set('from', from);
    params.set('to', to);
    navigate(`/bills/view?${params.toString()}`);
  };

  const fetchStatistics = async () => {
    const { from, to } = getDateRange(selectedPeriod);
    
    // Get current IST date for debugging
    const nowIST = new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' });
    console.log('📊 Fetching statistics for:', { 
      from, 
      to, 
      selectedPeriod,
      currentIST: nowIST,
      browserTime: new Date().toString()
    });
    
    if (!from || !to) {
      toast.error('Please select a valid date range');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('📡 Calling API endpoints...');
      const [totalsResponse, breakdownResponse, categoryResponse, billsSummaryResponse] = await Promise.all([
        billsApi.getDailyServiceChargesTotals({ from, to }),
        billsApi.getServiceBreakdown({ from, to }),
        billsApi.getCategoryBreakdown({ from, to }).catch(() => ({ success: false, data: [] })),
        billsApi.getAll({ limit: 1 }) // Just to get summary with used coupons
      ]);

      console.log('✅ API Responses:', { totalsResponse, breakdownResponse, categoryResponse });

      if (totalsResponse && totalsResponse.success) {
        console.log('📈 Daily Totals Data:', totalsResponse.data);
        console.log('📈 Full Response:', JSON.stringify(totalsResponse, null, 2));
        if (totalsResponse.data) {
          setDailyTotals(totalsResponse.data);
        } else {
          console.warn('⚠️ No data in response:', totalsResponse);
          setDailyTotals({
            grandTotal: 0,
            dailyTotals: [],
            previousDay: { date: '', total: 0 },
            dateRange: { from, to }
          });
        }
      } else {
        console.error('❌ Totals API failed or no response:', totalsResponse);
        // Even if failed, set empty data structure so UI still renders
        setDailyTotals({
          grandTotal: 0,
          dailyTotals: [],
          previousDay: { date: '', total: 0 },
          dateRange: { from, to }
        });
        // Don't throw error, just log it so UI can still show
        if (totalsResponse && totalsResponse.message) {
          console.warn('API Warning:', totalsResponse.message);
        }
      }

      if (breakdownResponse && breakdownResponse.success) {
        console.log('📊 Service Breakdown Data:', breakdownResponse.data);
        setServiceBreakdown(breakdownResponse.data || []);
      } else {
        console.warn('⚠️ Breakdown API failed or no response:', breakdownResponse);
        setServiceBreakdown([]);
      }

      if (categoryResponse && categoryResponse.success) {
        setCategoryBreakdown(categoryResponse.data || []);
      } else {
        setCategoryBreakdown([]);
      }
    } catch (err) {
      console.error('❌ Error fetching statistics:', err);
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        stack: err.stack
      });
      setError(err.message || 'Failed to load statistics');
      toast.error(err.message || 'Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPeriod === 'custom' && (!customFromDate || !customToDate)) {
      return;
    }
    fetchStatistics();
  }, [selectedPeriod, customFromDate, customToDate, browsedMonth]);

  const periodButtons = [
    { id: 'today', label: 'Today' },
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'dayBeforeYesterday', label: 'Day Before Yesterday' },
    { id: 'thisWeek', label: 'This Week' },
    { id: 'thisMonth', label: 'This Month' },
    { id: 'previousMonth', label: 'Previous Month' },
    { id: 'thisYear', label: 'This Year' },
    { id: 'previousYear', label: 'Previous Year' },
    { id: 'selectedMonth', label: 'Select Month' },
    { id: 'custom', label: 'Custom Range' },
  ];

  const pickerMonth = getPickerMonth();
  const [pickerYear, pickerMonthIndex] = pickerMonth.split('-').map(Number);
  const currentMonthValue = toYearMonth(getTodayIST());
  const currentYear = Number(currentMonthValue.split('-')[0]);
  const yearOptions = [];
  for (let year = currentYear; year >= Math.min(pickerYear || currentYear, 2018); year -= 1) {
    yearOptions.push(year);
  }

  const getTotalLabel = () => {
    if (selectedPeriod === 'selectedMonth') {
      return `${formatMonthLabel(browsedMonth)} Total`;
    }

    if (selectedPeriod === 'custom' && customFromDate && customToDate) {
      if (isFullCalendarMonth(customFromDate, customToDate)) {
        return `${formatMonthLabel(customFromDate.slice(0, 7))} Total`;
      }
      return `From ${customFromDate} to ${customToDate} Total`;
    }
    
    const periodMap = {
      'today': 'Today Total',
      'yesterday': 'Yesterday Total',
      'dayBeforeYesterday': 'Day Before Yesterday Total',
      'thisWeek': 'This Week Total',
      'thisMonth': 'Month Total',
      'previousMonth': 'Previous Month Total',
      'thisYear': 'This Year Total',
      'previousYear': 'Previous Year Total',
    };
    
    return periodMap[selectedPeriod] || 'Total';
  };

  const chartData = dailyTotals?.dailyTotals?.map(day => ({
    date: new Date(day.date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }),
    total: day.total,
    fullDate: day.date
  })) || [];

  return (
    <div className="counter-statistics-container">
      <div className="stats-header">
        <div>
          <h1>Counter Stats</h1>
          <p>Service Charges Profit Analysis & Trends</p>
        </div>
      </div>

      {/* Quick Period Selector */}
      <div className="period-selector">
        {periodButtons.map((period) => (
          <button
            key={period.id}
            className={`period-btn ${selectedPeriod === period.id ? 'active' : ''}`}
            onClick={() => {
              if (period.id === 'selectedMonth') {
                const { from } = getDateRange(selectedPeriod);
                setBrowsedMonth(from ? from.slice(0, 7) : toYearMonth(getTodayIST()));
              }
              setSelectedPeriod(period.id);
            }}
          >
            {period.label}
          </button>
        ))}
      </div>

      <div className={`month-navigator${selectedPeriod === 'selectedMonth' ? ' is-active' : ''}`}>
        <button
          type="button"
          className="month-nav-btn"
          onClick={() => shiftMonth(-1)}
          disabled={pickerMonth <= EARLIEST_MONTH}
          aria-label="Previous month"
        >
          ‹ Previous
        </button>
        <div className="month-picker-group">
          <select
            value={pickerMonthIndex || 1}
            onChange={(e) => {
              const month = String(e.target.value).padStart(2, '0');
              applyMonth(`${pickerYear}-${month}`);
            }}
            aria-label="Select month"
          >
            {MONTH_NAMES.map((name, index) => {
              const optionValue = `${pickerYear}-${String(index + 1).padStart(2, '0')}`;
              return (
                <option key={name} value={index + 1} disabled={optionValue > currentMonthValue}>
                  {name}
                </option>
              );
            })}
          </select>
          <select
            value={pickerYear || currentYear}
            onChange={(e) => {
              const month = String(pickerMonthIndex || 1).padStart(2, '0');
              applyMonth(`${e.target.value}-${month}`);
            }}
            aria-label="Select year"
          >
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="month-nav-btn"
          onClick={() => shiftMonth(1)}
          disabled={pickerMonth >= currentMonthValue}
          aria-label="Next month"
        >
          Next ›
        </button>
      </div>
      <p className="month-nav-hint">
        {formatMonthLabel(pickerMonth)}
        {pickerYear && pickerMonthIndex
          ? ` · ${getMonthBounds(pickerYear, pickerMonthIndex - 1).from} to ${getMonthBounds(pickerYear, pickerMonthIndex - 1).to}`
          : ''}
        {' '}— Previous / Next or select any month
      </p>

      {/* Custom Date Range */}
      {selectedPeriod === 'custom' && (
        <div className="custom-date-range">
          <input
            type="date"
            value={customFromDate}
            onChange={(e) => setCustomFromDate(e.target.value)}
            placeholder="From Date"
          />
          <input
            type="date"
            value={customToDate}
            onChange={(e) => setCustomToDate(e.target.value)}
            placeholder="To Date"
          />
          <button className="btn-fetch" onClick={fetchStatistics} disabled={!customFromDate || !customToDate}>
            Fetch
          </button>
        </div>
      )}

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading statistics...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchStatistics}>Retry</button>
        </div>
      )}

      {!loading && !error && dailyTotals ? (
        <>
          {/* Summary Cards */}
          <div className="summary-cards">
            <div
              className="summary-card card-primary summary-card-clickable"
              onClick={openBillsForRange}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openBillsForRange();
                }
              }}
            >
              <div className="card-icon">💰</div>
              <div className="card-content">
                <div className="card-label">{getTotalLabel()}</div>
                <div className="card-value">{formatCurrency(dailyTotals.grandTotal || 0)}</div>
                <div className="card-subtitle" style={{ fontWeight: 600, fontSize: '0.95em' }}>
                  Total Bills: {formatCount(getTotalBills(dailyTotals))}
                </div>
                {dailyTotals.previousDay && dailyTotals.previousDay.total > 0 ? (
                  <div className="card-subtitle">
                    Previous Day Total: {formatCurrency(dailyTotals.previousDay.total)}
                  </div>
                ) : (
                  <div className="card-subtitle">No previous day data</div>
                )}
                {dailyTotals.couponBillsTotal !== undefined && (
                  <div className="card-subtitle" style={{ marginTop: '8px', fontSize: '0.9em', opacity: dailyTotals.couponBillsTotal > 0 ? 0.9 : 0.6 }}>
                    Coupon Bills Total: {formatCurrency(dailyTotals.couponBillsTotal)}
                  </div>
                )}
                {dailyTotals.paymentTotals && (
                  <div className="card-subtitle" style={{ marginTop: '12px', fontSize: '0.9em', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '8px' }}>
                    <div style={{ marginBottom: '4px' }}>Total Paid: {formatCurrency(dailyTotals.paymentTotals.totalPaid)}</div>
                    <div style={{ marginBottom: '4px' }}>Total Due: {formatCurrency(dailyTotals.paymentTotals.totalDue)}</div>
                    <div style={{ fontWeight: '600', fontSize: '0.95em' }}>
                      Grand Total: {formatCurrency(dailyTotals.paymentTotals.grandTotal)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="summary-card card-teal">
              <div className="card-icon">🧾</div>
              <div className="card-content">
                <div className="card-label">Total Bills</div>
                <div className="card-value">{formatCount(getTotalBills(dailyTotals))}</div>
                <div className="card-subtitle">
                  Bills in {dailyTotals.dateRange?.from} to {dailyTotals.dateRange?.to}
                </div>
              </div>
            </div>

            <div className="summary-card card-success">
              <div className="card-icon">📊</div>
              <div className="card-content">
                <div className="card-label">Days Active</div>
                <div className="card-value">{dailyTotals.dailyTotals?.length || 0}</div>
                <div className="card-subtitle">
                  {dailyTotals.dateRange?.from} to {dailyTotals.dateRange?.to}
                </div>
              </div>
            </div>

            <div className="summary-card card-info">
              <div className="card-icon">📈</div>
              <div className="card-content">
                <div className="card-label">Avg Daily</div>
                <div className="card-value">
                  {dailyTotals.dailyTotals?.length > 0
                    ? formatCurrency(dailyTotals.grandTotal / dailyTotals.dailyTotals.length)
                    : formatCurrency(0)}
                </div>
                <div className="card-subtitle">Per day average</div>
              </div>
            </div>

            <div className="summary-card card-warning">
              <div className="card-icon">🔥</div>
              <div className="card-content">
                <div className="card-label">Top Service</div>
                <div className="card-value">
                  {serviceBreakdown[0]?.serviceName?.substring(0, 15) || 'N/A'}
                </div>
                <div className="card-subtitle">
                  {serviceBreakdown[0] ? formatCurrency(serviceBreakdown[0].totalServiceCharges) : 'No data'}
                </div>
              </div>
            </div>

            <div className="summary-card card-purple">
              <div className="card-icon">🎫</div>
              <div className="card-content">
                <div className="card-label">Used Coupons</div>
                <div className="card-value">{usedCouponsCount}</div>
                <div className="card-subtitle">Total coupons redeemed</div>
              </div>
            </div>
          </div>

          {/* Charts Section */}
          <div className="charts-grid">
            {/* Daily Sales Line Chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Daily Sales Trend</h3>
                <span className="chart-subtitle">Service Charges Profit Over Time</span>
              </div>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="date" 
                      stroke="#6b7280"
                      style={{ fontSize: '12px' }}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrencyFull(value)}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="total" 
                      stroke="#3b82f6" 
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Service Charges"
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                  <p>No data available for the selected period</p>
                </div>
              )}
            </div>

            {/* Service Breakdown Bar Chart */}
            <div className="chart-card">
              <div className="chart-header">
                <h3>Top Services</h3>
                <span className="chart-subtitle">Service-wise Profit Breakdown</span>
              </div>
              {serviceBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={serviceBreakdown.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="serviceName" 
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      stroke="#6b7280"
                      style={{ fontSize: '11px' }}
                    />
                    <YAxis 
                      stroke="#6b7280"
                      tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}
                      style={{ fontSize: '12px' }}
                    />
                    <Tooltip 
                      formatter={(value) => formatCurrencyFull(value)}
                      contentStyle={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px' }}
                    />
                    <Legend />
                    <Bar dataKey="totalServiceCharges" fill="#10b981" name="Service Charges" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                  <p>No service data available for the selected period</p>
                </div>
              )}
            </div>
          </div>

          {/* Main Category Breakdown */}
          {categoryBreakdown.length > 0 && (
            <div className="service-breakdown-table">
              <h3>Main Category Analytics</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Main Category</th>
                      <th>Type</th>
                      <th>Bills Count</th>
                      <th>Total</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryBreakdown.map((row, index) => {
                      const percentage = dailyTotals.grandTotal > 0
                        ? ((row.totalServiceCharges / dailyTotals.grandTotal) * 100).toFixed(1)
                        : 0;
                      return (
                        <tr key={`cat-${index}`}>
                          <td>{row.mainCategory}</td>
                          <td>{row.itemKind}</td>
                          <td>{row.billCount}</td>
                          <td className="amount-cell">{formatCurrencyFull(row.totalServiceCharges)}</td>
                          <td>
                            <div className="percentage-bar">
                              <div
                                className="percentage-fill"
                                style={{ width: `${percentage}%`, backgroundColor: COLORS[index % COLORS.length] }}
                              ></div>
                              <span>{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Service Breakdown Table */}
          {serviceBreakdown.length > 0 && (
            <div className="service-breakdown-table">
              <h3>Service-wise Breakdown</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Service Name</th>
                      <th>Bills Count</th>
                      <th>Total</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceBreakdown.map((service, index) => {
                      const percentage = dailyTotals.grandTotal > 0
                        ? ((service.totalServiceCharges / dailyTotals.grandTotal) * 100).toFixed(1)
                        : 0;
                      return (
                        <tr key={index}>
                          <td>{service.serviceName}</td>
                          <td>{service.billCount}</td>
                          <td className="amount-cell">{formatCurrencyFull(service.totalServiceCharges)}</td>
                          <td>
                            <div className="percentage-bar">
                              <div 
                                className="percentage-fill" 
                                style={{ width: `${percentage}%`, backgroundColor: COLORS[index % COLORS.length] }}
                              ></div>
                              <span>{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily Totals Table */}
          {dailyTotals.dailyTotals && dailyTotals.dailyTotals.length > 0 && (
            <div className="daily-totals-table">
              <h3>Daily Breakdown</h3>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Bills</th>
                      <th>Service Charges (Profit)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyTotals.dailyTotals.map((day, index) => (
                      <tr key={index}>
                        <td>{day.date}</td>
                        <td>{formatCount(day.billCount)}</td>
                        <td className="amount-cell">{formatCurrencyFull(day.total)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td><strong>{formatCount(getTotalBills(dailyTotals))}</strong></td>
                      <td className="amount-cell"><strong>{formatCurrencyFull(dailyTotals.grandTotal)}</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Cross-day / same-period refunds — mention only; no deduction on refund day */}
          {Array.isArray(dailyTotals.refundsNoted) && dailyTotals.refundsNoted.length > 0 && (
            <div className="daily-totals-table" style={{ marginTop: 24 }}>
              <h3>Refunds noted in this period</h3>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: '#6b7280' }}>
                Deducted from the original bill date counter. Cross-day refunds are listed here only — they do not reduce this period&apos;s total.
              </p>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Refunded on</th>
                      <th>Bill</th>
                      <th>Bill date</th>
                      <th>Refund</th>
                      <th>Kept fee</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyTotals.refundsNoted.map((refund) => (
                      <tr key={refund.id}>
                        <td>
                          {refund.refundedAt
                            ? new Date(refund.refundedAt).toLocaleDateString('en-IN')
                            : '—'}
                        </td>
                        <td>{refund.billNumber || refund.billId || '—'}</td>
                        <td>
                          {refund.billDate
                            ? new Date(`${refund.billDate}T00:00:00+05:30`).toLocaleDateString('en-IN')
                            : '—'}
                          {refund.isCrossDay ? (
                            <span style={{ marginLeft: 6, color: '#b45309', fontSize: 12 }}>
                              (earlier bill)
                            </span>
                          ) : null}
                        </td>
                        <td className="amount-cell">{formatCurrencyFull(refund.refundAmount)}</td>
                        <td className="amount-cell">{formatCurrencyFull(refund.processingFeeRetained)}</td>
                        <td style={{ maxWidth: 220, fontSize: 12, color: '#4b5563' }}>
                          {refund.isCrossDay
                            ? `Noted only — deducted on bill date ${refund.billDate || ''}`
                            : 'Same-day — already in bill-date total'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : !loading && !error && !dailyTotals && (
        <div className="no-data-state">
          <div className="no-data-icon">📊</div>
          <h3>No Data Available</h3>
          <p>Select a date range to view statistics</p>
          <button className="btn-fetch" onClick={fetchStatistics}>
            Load Statistics
          </button>
        </div>
      )}
    </div>
  );
};

export default CounterStatistics;
